import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  CircularProgress,
  Button,
  Tooltip,
  Divider,
  Select,
  MenuItem,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddIcon from '@material-ui/icons/Add';
import SortIcon from '@material-ui/icons/Sort';
import AssessmentIcon from '@material-ui/icons/Assessment';
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import SpeedIcon from '@material-ui/icons/Speed';
import { MarketplaceProvider, useMarketplace } from './MarketplaceContext';
import { FavoritesProvider } from './FavoritesContext';
import { MarketplaceItem, ItemType } from './types';
import { TYPE_META, isComingSoonType } from './typeMetadata';
import { TypeIcon } from './TypeIcon';
import { MarketplaceCard } from './MarketplaceCard';
import { ItemDetailView } from './ItemDetailView';
import { ContributeDialog } from './ContributeDialog';
import { MARKETPLACE_ADMIN_EMAILS } from './marketplace-admins';
import { useMarketplaceFetchApi } from './marketplaceFetch';

// ─── Sort options ─────────────────────────────────────────────────────────────

export type SortOption = 'popularity' | 'name' | 'newest' | 'type';

const SORT_LABELS: Record<SortOption, string> = {
  popularity: 'Popularity',
  name: 'Name (A-Z)',
  newest: 'Newest',
  type: 'Type',
};

const VALID_SORT_OPTIONS: SortOption[] = ['popularity', 'name', 'newest', 'type'];

function parseSortParam(param: string | null): SortOption {
  if (param && VALID_SORT_OPTIONS.includes(param as SortOption)) {
    return param as SortOption;
  }
  return 'popularity';
}

export function sortItems(items: MarketplaceItem[], sort: SortOption): MarketplaceItem[] {
  const sorted = [...items];
  switch (sort) {
    case 'popularity':
      return sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
    case 'name':
      return sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
    case 'newest':
      // Reverses the registry order, assuming items are appended chronologically.
      // TODO: replace with `sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))`
      // once a `createdAt` field is added to MarketplaceItem and populated in the registry.
      return sorted.reverse();
    case 'type':
      return sorted.sort((a, b) => a.type.localeCompare(b.type));
    default:
      return sorted;
  }
}

// ─── Verint LUX color palette ─────────────────────────────────────────────────

const B = {
  darkest: '#0D3E86',
  dark: '#0057BF',
  mid: '#007AFF',
  primary: '#007AFF',
  bright: '#85CBFF',
  pale: '#E1F4FF',
};

type ViewType = 'all' | ItemType;

const MARKETPLACE_TYPES = Object.keys(TYPE_META) as ItemType[];

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: '100vh',
    background: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
  },

  // ── HEADER ──
  header: {
    background: `linear-gradient(160deg, #061A38 0%, ${B.darkest} 35%, ${B.dark} 70%, #3D8EDB 100%)`,
    padding: theme.spacing(5, 4, 4),
    textAlign: 'center',
    flexShrink: 0,
  },
  heroTitle: {
    fontWeight: 900,
    fontSize: '2.4rem',
    color: '#fff',
    letterSpacing: '-0.5px',
    marginBottom: theme.spacing(0.75),
  },
  heroSubtitle: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: '1rem',
    marginBottom: theme.spacing(3),
    fontWeight: 400,
  },
  searchWrap: {
    maxWidth: 540,
    margin: '0 auto',
    display: 'block',
    '& .MuiOutlinedInput-root': {
      borderRadius: 32,
      color: '#fff',
      background: 'rgba(255,255,255,0.1)',
      '& fieldset': { border: '1.5px solid rgba(255,255,255,0.25)' },
      '&:hover fieldset': { border: '1.5px solid rgba(255,255,255,0.55)' },
      '&.Mui-focused fieldset': { border: '2px solid rgba(255,255,255,0.85)' },
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: 'rgba(255,255,255,0.55)',
    },
    '& input': { color: '#fff', textAlign: 'left' },
    '& input::placeholder': { color: 'rgba(255,255,255,0.45)' },
  },

  // ── STAT PILLS (image 2 style) ──
  statsRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    flexWrap: 'wrap',
  },
  statCard: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1.5, 3.5),
    textAlign: 'center',
    minWidth: 110,
    cursor: 'pointer',
    transition: 'background 0.18s, border-color 0.18s, transform 0.12s',
    '&:hover': {
      background: 'rgba(255,255,255,0.16)',
      borderColor: 'rgba(255,255,255,0.35)',
      transform: 'translateY(-1px)',
    },
  },
  statCardDisabled: {
    cursor: 'not-allowed',
    '&:hover': {
      background: 'rgba(255,255,255,0.08)',
      borderColor: 'rgba(255,255,255,0.15)',
      transform: 'none',
    },
  },
  statCardActive: {
    background: `rgba(0, 80, 179, 0.55)`,
    borderColor: `${B.pale}55`,
    '&:hover': { background: `rgba(0, 80, 179, 0.7)` },
  },
  statCount: {
    fontWeight: 800,
    fontSize: '1.9rem',
    color: '#fff',
    lineHeight: 1.05,
  },
  statCountComingSoon: {
    fontWeight: 800,
    fontSize: '0.82rem',
    color: '#fff',
    lineHeight: 1.2,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    minHeight: 38,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center' as const,
  },
  statLabel: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: '0.68rem',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    marginTop: 3,
    fontWeight: 600,
  },
  statRefresh: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.18s',
    '&:hover': { background: 'rgba(255,255,255,0.18)' },
  },

  // ── CONTENT ──
  content: {
    flex: 1,
    maxWidth: 1280,
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(4, 3),
    boxSizing: 'border-box',
  },

  // ── BREADCRUMB ──
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  breadcrumbLink: {
    cursor: 'pointer',
    color: B.dark,
    fontWeight: 600,
    fontSize: 'inherit',
    fontFamily: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: '#fff',
    border: '1.5px solid #D0D5DD',
    borderRadius: 8,
    padding: theme.spacing(0.5, 1.5),
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: B.dark,
      background: '#F5FAFF',
      boxShadow: '0 1px 3px rgba(0,87,191,0.12)',
    },
  },
  breadcrumbSep: { opacity: 0.35, fontSize: '0.9rem' },

  // ── CATEGORY FILTER ──
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    flexWrap: 'wrap',
  },
  filterSearch: {
    width: 240,
    '& .MuiOutlinedInput-root': { borderRadius: 24 },
  },
  sortSelect: {
    minWidth: 170,
    '& .MuiOutlinedInput-root': { borderRadius: 24, fontSize: '0.85rem' },
    '& .MuiSelect-outlined': { paddingTop: 8, paddingBottom: 8 },
  },
  sortWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  countLabel: {
    fontSize: '0.82rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },

  // ── SECTION ──
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  viewAllBtn: {
    fontSize: '0.8rem',
    color: B.pale,
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0.25, 1.25),
    borderRadius: 14,
    '&:hover': { background: `${B.primary}18` },
  },
  sectionDivider: { margin: theme.spacing(3.5, 0, 0) },
  noResults: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    paddingTop: theme.spacing(5),
  },
  loadingBox: { textAlign: 'center', padding: theme.spacing(6, 0) },
  contributeBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 10,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(2.5),
    boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
    backgroundColor: '#fff',
    color: '#0D3E86',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.92)',
    },
  },
  analyticsBtn: {
    textTransform: 'none' as const,
    fontWeight: 600,
    borderRadius: 8,
    fontSize: '0.75rem',
    padding: '6px 14px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.22)',
    color: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(8px)',
    '&:hover': {
      background: 'rgba(255,255,255,0.18)',
      color: '#fff',
      borderColor: 'rgba(255,255,255,0.4)',
    },
  },
  headerActions: {
    position: 'absolute' as const,
    top: 16,
    right: 16,
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
}));

// ─── "All" landing view ───────────────────────────────────────────────────────

function AllView({
  search,
  sort,
  onCategory: _onCategory,
  filterTokenEfficient,
}: {
  search: string;
  sort: SortOption;
  onCategory: (v: ItemType) => void;
  filterTokenEfficient: boolean;
}) {
  const classes = useStyles();
  const { items } = useMarketplace();

  const filterList = (list: MarketplaceItem[]) => {
    const q = search.toLowerCase();
    let r = list;
    if (q) {
      r = r.filter(
        i =>
          i.displayName.toLowerCase().includes(q) ||
          i.description.toLowerCase().includes(q) ||
          i.keywords.some(k => k.toLowerCase().includes(q)),
      );
    }
    if (filterTokenEfficient) {
      r = r.filter(i => i.tokenEfficient === true);
    }
    return r;
  };

  const sections: Array<{ type: ItemType; items: MarketplaceItem[] }> =
    MARKETPLACE_TYPES.map(type => ({
      type,
      items: sortItems(filterList(items.filter(i => i.type === type)), sort),
    }));

  const totalVisible = sections.reduce((s, sec) => s + sec.items.length, 0);

  if (totalVisible === 0 && (search || filterTokenEfficient)) {
    return (
      <Typography className={classes.noResults}>
        No results match your criteria.
      </Typography>
    );
  }

  return (
    <>
      {sections.map(({ type, items }, idx) => {
        const meta = TYPE_META[type];
        return (
          <Box key={type}>
            {idx > 0 && <Divider className={classes.sectionDivider} />}
            <Box
              className={classes.sectionHeader}
              style={{ marginTop: idx > 0 ? 32 : 0 }}
            >
              <Typography
                className={classes.sectionTitle}
                component="div"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <TypeIcon
                  iconName={meta.iconName}
                  fontSize="small"
                  style={{ color: meta.color }}
                />
                &nbsp;{meta.label}s
                <Chip
                  label={items.length}
                  size="small"
                  style={{
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 18,
                    minWidth: 20,
                    position: 'relative',
                    top: 4,
                  }}
                />
              </Typography>
            </Box>
            {items.length === 0 ? (
              <Typography className={classes.noResults}>
                No {meta.label.toLowerCase()}s match your search.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {items.map(item => (
                  <Grid item xs={12} sm={6} md={4} xl={3} key={item.id}>
                    <MarketplaceCard item={item} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        );
      })}
    </>
  );
}

// ─── Category view (all item types) ───────────────────────────────────────────

function CategoryView({
  type,
  search,
  filterText,
  sort,
  filterTokenEfficient,
}: {
  type: ItemType;
  search: string;
  filterText: string;
  sort: SortOption;
  filterTokenEfficient: boolean;
}) {
  const classes = useStyles();
  const { items } = useMarketplace();
  const allItems = items.filter(i => i.type === type);

  const filtered = useMemo(() => {
    let r = allItems;
    const q = search.toLowerCase();
    if (q) {
      r = r.filter(
        i =>
          i.displayName.toLowerCase().includes(q) ||
          i.description.toLowerCase().includes(q) ||
          i.keywords.some(k => k.toLowerCase().includes(q)),
      );
    }
    const kq = filterText.toLowerCase();
    if (kq) {
      r = r.filter(
        i =>
          i.keywords.some(k => k.toLowerCase().includes(kq)) ||
          i.displayName.toLowerCase().includes(kq) ||
          i.description.toLowerCase().includes(kq),
      );
    }
    if (filterTokenEfficient) {
      r = r.filter(i => i.tokenEfficient === true);
    }
    return sortItems(r, sort);
  }, [allItems, search, filterText, sort, filterTokenEfficient]);

  return (
    <>
      <Typography className={classes.countLabel}>
        {filtered.length} of {allItems.length}{' '}
        {TYPE_META[type].label.toLowerCase()}s
        {filterText && ` matching "${filterText}"`}
      </Typography>
      {filtered.length === 0 ? (
        <Typography className={classes.noResults}>
          No results match your criteria.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {filtered.map(item => (
            <Grid item xs={12} sm={6} md={4} xl={3} key={item.id}>
              <MarketplaceCard item={item} />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function AiMarketplacePageInner() {
  const classes = useStyles();
  const { items, loading, error, refresh } = useMarketplace();
  const location = useLocation();
  const navigate = useNavigate();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi     = useMarketplaceFetchApi();
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeView, setActiveView] = useState<ViewType>('all');
  const [search, setSearch] = useState('');
  const [filterText, setFilterText] = useState('');
  const [contributeOpen, setContributeOpen] = useState(false);

  // Token-efficient filter: persisted in URL
  const qsTE = new URLSearchParams(location.search);
  const [filterTokenEfficient, setFilterTokenEfficient] = useState(qsTE.get('tokenEfficient') === 'true');

  // Read sort from URL query param, default to 'popularity'.
  const qs = new URLSearchParams(location.search);
  const sort = parseSortParam(qs.get('sort'));

  const handleSortChange = (newSort: SortOption) => {
    const params = new URLSearchParams(location.search);
    if (newSort === 'popularity') {
      params.delete('sort');
    } else {
      params.set('sort', newSort);
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleTokenEfficientToggle = () => {
    const next = !filterTokenEfficient;
    setFilterTokenEfficient(next);
    const params = new URLSearchParams(location.search);
    if (next) params.set('tokenEfficient', 'true');
    else params.delete('tokenEfficient');
    navigate({ search: params.toString() }, { replace: true });
  };

  useEffect(() => {
    document.title = 'AI Marketplace | Verint Dev Portal';
  }, []);

  // Fire-and-forget: record this page visit for the unique-visitors KPI.
  useEffect(() => {
    (async () => {
      try {
        const profile = await identityApi.getProfileInfo().catch(() => null);
        const userEntityRef = await identityApi.getCredentials()
          .then(c => (c as any)?.token ? undefined : undefined)
          .catch(() => undefined);
        // Prefer the full entity ref from the identity API if available.
        let userRef = (userEntityRef as string | undefined) ?? '';
        if (!userRef && profile?.email) {
          userRef = `user:default/${profile.email.split('@')[0]}`;
        }
        const baseUrl = await discoveryApi.getBaseUrl('marketplace');
        await fetchApi.fetch(`${baseUrl}/visitors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_ref: userRef || undefined }),
        });
      } catch {
        // analytics is non-critical — never break the page
      }
    })();
  // Run once per mount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    identityApi.getProfileInfo().then(profile => {
      setIsAdmin(MARKETPLACE_ADMIN_EMAILS.has((profile.email ?? '').toLowerCase()));
    }).catch(() => setIsAdmin(false));
  }, [identityApi]);

  // When both ?type and ?id are in the URL, delegate to the detail view.
  const showDetailView = Boolean(qs.get('type') && qs.get('id'));

  // Restore scroll position when returning from the detail view to the list.
  // We wait until loading completes so the page is tall enough for scrollTo.
  // The saved value includes a timestamp; entries older than 5 min are discarded
  // to avoid jumping to a stale offset after reload or external navigation.
  useEffect(() => {
    if (!showDetailView && !loading) {
      const saved = sessionStorage.getItem('mp-scroll-y');
      if (saved) {
        sessionStorage.removeItem('mp-scroll-y');
        const [rawY, rawTs] = saved.split(':');
        const y = parseInt(rawY, 10);
        const ts = parseInt(rawTs, 10);
        const MAX_AGE_MS = 5 * 60 * 1000;
        if (y > 0 && ts && Date.now() - ts < MAX_AGE_MS) {
          window.requestAnimationFrame(() => window.scrollTo(0, y));
        }
      }
    }
  }, [showDetailView, loading]);

  const filteredItems = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return items;
    return items.filter(
      i =>
        i.displayName.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q) ||
        i.keywords.some(k => k.toLowerCase().includes(q)),
    );
  }, [items, search]);

  const visibleAllCount = filteredItems.filter(
    item => !isComingSoonType(item.type),
  ).length;

  if (showDetailView) {
    return <ItemDetailView />;
  }

  const statCards: Array<{
    id: ViewType;
    label: string;
    count: number;
    comingSoon: boolean;
  }> = [
    { id: 'all', label: 'All', count: visibleAllCount, comingSoon: false },
    ...MARKETPLACE_TYPES.map(type => ({
      id: type as ViewType,
      label: `${TYPE_META[type].label}s`,
      count: filteredItems.filter(i => i.type === type).length,
      comingSoon: isComingSoonType(type),
    })),
  ];

  const currentMeta =
    activeView !== 'all' ? TYPE_META[activeView as ItemType] : null;

  return (
    <Box className={classes.root}>
      {/* ── Header ── */}
      <Box className={classes.header} style={{ position: 'relative' }}>
        <Box className={classes.headerActions}>
          <Tooltip title="Your saved favorites">
            <Button
              className={classes.analyticsBtn}
              startIcon={<BookmarkBorderIcon style={{ fontSize: '0.9rem' }} />}
              onClick={() => navigate('/ai-marketplace/favorites')}
            >
              Favorites
            </Button>
          </Tooltip>
          {isAdmin && (
            <Tooltip title="Marketplace Analytics — Admins only" placement="left">
              <Button
                className={classes.analyticsBtn}
                startIcon={<AssessmentIcon style={{ fontSize: '0.9rem' }} />}
                onClick={() => navigate('/ai-marketplace/dashboard')}
              >
                Analytics
              </Button>
            </Tooltip>
          )}
        </Box>
        <Typography className={classes.heroTitle}>AI Marketplace</Typography>
        <Typography className={classes.heroSubtitle}>
          Discover and install custom AI marketplace content for GitHub Copilot
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          className={classes.contributeBtn}
          onClick={() => setContributeOpen(true)}
        >
          Contribute an Item
        </Button>

        {/* Centered search */}
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search agents, skills, prompts, templates, models…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={classes.searchWrap}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Stat cards — image 2 style */}
        <Box className={classes.statsRow}>
          {statCards.map(({ id, label, count, comingSoon }) => (
            <Box
              key={id}
              data-testid={`stat-card-${id}`}
              className={`${classes.statCard} ${comingSoon ? classes.statCardDisabled : ''} ${activeView === id && !comingSoon ? classes.statCardActive : ''}`}
              onClick={comingSoon ? undefined : () => setActiveView(id)}
              aria-disabled={comingSoon}
              style={comingSoon ? { opacity: 0.60, cursor: 'not-allowed', borderStyle: 'dashed' } : undefined}
            >
              <Typography
                className={
                  comingSoon ? classes.statCountComingSoon : classes.statCount
                }
              >
                {comingSoon ? 'Coming Soon' : loading ? '…' : count}
              </Typography>
              <Typography className={classes.statLabel}>{label}</Typography>
            </Box>
          ))}
          <Tooltip title="Refresh from GitHub">
            <Box className={classes.statRefresh} onClick={refresh}>
              <RefreshIcon
                style={{ color: 'rgba(255,255,255,0.72)', fontSize: 22 }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box className={classes.content}>
        {/* Sort dropdown — always visible */}
        {!showDetailView && (
          <Box className={classes.filterBar}>
            {/* Breadcrumb + inline filter when in a category */}
            {activeView !== 'all' && currentMeta && (
              <>
                <Typography
                  component="button"
                  className={classes.breadcrumbLink}
                  onClick={() => {
                    setActiveView('all');
                    setFilterText('');
                  }}
                >
                  ← AI Marketplace
                </Typography>
                <Typography component="span" className={classes.breadcrumbSep}>
                  /
                </Typography>
                <Typography
                  component="span"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <TypeIcon iconName={currentMeta.iconName} fontSize="small" />
                  &nbsp;{currentMeta.label}s
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Filter by keyword…"
                  value={filterText}
                  onChange={e => setFilterText(e.target.value)}
                  className={classes.filterSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" style={{ opacity: 0.5 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
            <Box className={classes.sortWrapper}>
              <SortIcon fontSize="small" style={{ opacity: 0.6 }} />
              <Select
                variant="outlined"
                value={sort}
                onChange={e => handleSortChange(e.target.value as SortOption)}
                className={classes.sortSelect}
                data-testid="sort-select"
                inputProps={{ 'aria-label': 'Sort by' }}
              >
                {VALID_SORT_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {SORT_LABELS[opt]}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Tooltip title="Show only items that passed token-efficiency scoring">
              <Chip
                icon={<SpeedIcon style={{ fontSize: 14, color: filterTokenEfficient ? '#09A761' : undefined }} />}
                label="Token-Efficient"
                size="small"
                clickable
                onClick={handleTokenEfficientToggle}
                style={{
                  backgroundColor: filterTokenEfficient ? 'rgba(9,167,97,0.1)' : undefined,
                  color: filterTokenEfficient ? '#09A761' : undefined,
                  border: filterTokenEfficient ? '1px solid rgba(9,167,97,0.25)' : '1px solid transparent',
                  fontWeight: filterTokenEfficient ? 700 : 400,
                  transition: 'all 0.15s',
                }}
              />
            </Tooltip>
          </Box>
        )}

        {loading && (
          <Box className={classes.loadingBox}>
            <CircularProgress />
            <Typography
              style={{ marginTop: 12, opacity: 0.5, fontSize: '0.9rem' }}
            >
              Loading marketplace…
            </Typography>
          </Box>
        )}
        {!loading && !!error && (
          <Box textAlign="center" pt={4}>
            <Typography color="error">Failed to load: {error}</Typography>
            <Button
              variant="outlined"
              onClick={refresh}
              style={{ marginTop: 12 }}
            >
              Retry
            </Button>
          </Box>
        )}
        {!loading && !error && activeView === 'all' && (
          <AllView search={search} sort={sort} onCategory={v => setActiveView(v)} filterTokenEfficient={filterTokenEfficient} />
        )}
        {!loading && !error && activeView !== 'all' && (
          <CategoryView
            type={activeView as ItemType}
            search={search}
            filterText={filterText}
            sort={sort}
            filterTokenEfficient={filterTokenEfficient}
          />
        )}
      </Box>
      <ContributeDialog
        open={contributeOpen}
        onClose={() => setContributeOpen(false)}
      />
    </Box>
  );
}

export function AiMarketplacePage() {
  return (
    <MarketplaceProvider>
      <FavoritesProvider>
        <AiMarketplacePageInner />
      </FavoritesProvider>
    </MarketplaceProvider>
  );
}
